import { LinkOutlined, UserOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import {
  AvatarDropdown,
  AvatarName,
} from '@/components';
import { currentUser as queryCurrentUser, refreshToken } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import '@ant-design/v5-patch-for-react-19';
import { fetchServerMenu } from '@/services/ant-design-pro/menu';
import type { ServerMenuDataItem } from '@/types/menu';
import fixMenuItemIcon from '@/utils/fixMenuItemIcon';
import { extractValidRoutesFromMenu, isRouteValid } from '@/utils/menu';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

const authHeaderInterceptor = (url: string, options: RequestConfig) => {
  const accessToken = sessionStorage.getItem('accessToken') || '';
  const authHeader = { Authorization: `Bearer ${accessToken}` };
  return {
    url: `${url}`,
    options: { ...options, interceptors: true, headers: authHeader },
  };
};

export const request: RequestConfig = {
  // 新增自动添加AccessToken的请求前拦截器
  requestInterceptors: [authHeaderInterceptor],
};

// 定时刷新 token 的函数
let tokenRefreshTimer: NodeJS.Timeout | null = null;

const startTokenRefreshTimer = () => {
  // 清除之前的定时器
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
  }
  
  // 设置定时器，每30分钟刷新一次 token
  const refreshInterval = 30 * 60 * 1000; // 30分钟
  
  tokenRefreshTimer = setInterval(async () => {
    try {
      const response = await refreshToken();
      if (response.code === 200) {
        // 更新 sessionStorage 中的 token
        sessionStorage.setItem('accessToken', response.data.token);
      }
    } catch (error) {
      console.error('刷新 token 失败:', error);
      // 刷新失败时，清除定时器，用户需要重新登录
      clearInterval(tokenRefreshTimer as NodeJS.Timeout);
      tokenRefreshTimer = null;
    }
  }, refreshInterval);
};

// 清除 token 刷新定时器
const clearTokenRefreshTimer = () => {
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  menuData?: ServerMenuDataItem[];
  validRoutes?: string[]; // 新增：合法路由列表
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  const fetchMenuData = async () => {
    try {
      const msg = await fetchServerMenu({
        skipErrorHandler: true,
      });
      return fixMenuItemIcon(msg.data);
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const menuData = await fetchMenuData();
    const validRoutes = menuData ? extractValidRoutesFromMenu(menuData) : [];
    
    // 用户登录成功后，启动 token 刷新定时器
    if (currentUser) {
      startTokenRefreshTimer();
    }
    
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      menuData,
      validRoutes,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
    ],
    avatarProps: {
      icon: <UserOutlined />,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.username,
    },
    menuDataRender: () => initialState?.menuData || [],
    onPageChange: () => {
      const { location } = history;
      const { pathname } = location;
      // 如果用户跳转到登录页面，清除 token 刷新定时器
      if (pathname === loginPath) {
        clearTokenRefreshTimer();
      }
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && pathname !== loginPath) {
        history.push(loginPath);
      }
      const validRoutes = initialState?.validRoutes || [];
      if (validRoutes.length === 0) {
        history.push(loginPath);
      }
      // 校验路由合法性
      const WHITE_LIST = [
        '/404', // 不存在页
      ];
      const isValid = isRouteValid(pathname, validRoutes, WHITE_LIST);
      if (!isValid) {
        // 避免重复跳转（防止死循环）
        if (pathname !== '/404') {
          history.replace('/404'); // 使用 replace 避免路由栈冗余
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
        </>
      );
    },
    ...initialState?.settings,
  };
};
